import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
// useChat removed 2026-05.
// useEmailPanel removed 2026-05.
// StartChatFromEmailModal removed 2026-05.
import { FeedEmailData } from '../../pages/mail/components/EmailFeedContent';
import { sanitizeHtml, escapeHtml } from '../../utils/sanitizeHtml';

interface Props {
  email: FeedEmailData | null;
  show: boolean;
  onHide: () => void;
  isLoading?: boolean;
}

const EmailOverlayModal: React.FC<Props> = ({ email, show, onHide, isLoading }): JSX.Element => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<{ url: string; name: string }[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Chat-from-email state + hooks removed 2026-05.

  const adjustIframeHeight = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      setTimeout(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          const height = iframeRef.current.contentWindow.document.body.scrollHeight;
          iframeRef.current.style.height = `${height + 30}px`;
        }
      }, 100);
    }
  };

  const getStyledHtml = (content: string) => {
    // Log image sources for debugging
    const imgMatches = content.match(/<img[^>]+src="([^"]+)"/g);
    if (imgMatches) {
      console.log('🖼️ Images found in email:', imgMatches.map(match => {
        const srcMatch = match.match(/src="([^"]+)"/);
        return srcMatch ? srcMatch[1] : 'unknown';
      }));
    }

    return `
    <html>
      <head>
        <style>
          body {
            background-color: var(--bs-body-bg, #fff);
            color: var(--bs-body-color, #212529);
            font-family: inherit;
            padding: 1rem;
            margin: 0;
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
          }
          a {
            color: var(--bs-primary, #007bff);
          }
          img {
            max-width: 100%;
            height: auto;
            border: 1px solid #ddd;
            background-color: #f9f9f9;
          }
          img[src=""], img:not([src]) {
            display: none;
          }
          /* Style for broken images */
          img::before {
            content: "🖼️ Image not available";
            display: block;
            padding: 10px;
            background-color: #f8f9fa;
            border: 1px dashed #dee2e6;
            color: #6c757d;
            font-size: 14px;
            text-align: center;
          }
        </style>
        <base target="_blank">
      </head>
      <body>
        ${content}
      </body>
    </html>
    `;
  };

  useEffect(() => {
    if (show && email) {
      // Email data processing

      // Set attachments from the complete email object
      if (email.attachments && email.attachments.length > 0) {
        const uiAttachments = email.attachments.map((att: any) => ({
          name: att.filename,
          url: att.url,
        }));
        setAttachments(uiAttachments);
      } else {
        setAttachments([]);
      }

      // Set body content, with robust fallbacks
      const emailHtmlUrl = email.html?.trim();
      const emailText = email.text?.trim();
      const emailSnippet = email.message?.trim(); // Use message instead of snippet

      console.log('🔍 Email content decision:', {
        emailId: email.id,
        hasHtmlUrl: !!emailHtmlUrl,
        htmlUrl: emailHtmlUrl,
        hasText: !!emailText,
        hasSnippet: !!emailSnippet,
        textLength: emailText?.length || 0
      });

      // Check if HTML is a URL (starts with http)
      if (emailHtmlUrl && emailHtmlUrl.startsWith('http')) {
        console.log('📥 Fetching HTML content from URL for email:', email.id);
        // Fetch HTML content from the URL
        fetch(emailHtmlUrl)
          .then(response => response.text())
          .then(htmlContent => {
            // Process CID references if attachments are available
            let processedHtml = htmlContent;
            
            if (email.attachments && email.attachments.length > 0) {
              console.log('🖼️ Processing CID references with attachments:', email.attachments);
              
              // Replace cid: references with actual attachment URLs
              email.attachments.forEach((attachment: any) => {
                if (attachment.cid && attachment.url) {
                  // Literal replace: attachment.cid is the (attacker-controlled) email's
                  // Content-ID and must never be compiled as a RegExp (ReDoS in the
                  // owner's browser tab).
                  processedHtml = processedHtml.split(`cid:${attachment.cid}`).join(attachment.url);
                  console.log(`🖼️ Replaced cid:${attachment.cid} with ${attachment.url}`);
                }
              });
            }
            
            // SECURITY: sanitise AFTER the CID rewrite so the final markup — including
            // any URL we substituted in — is what gets sanitised.
            setHtmlContent(getStyledHtml(sanitizeHtml(processedHtml)));
          })
          .catch(error => {
            console.error('Failed to fetch HTML content:', error);
            // Fall back to text content
            if (emailText) {
              setHtmlContent(getStyledHtml(`<div style="white-space: pre-wrap;">${escapeHtml(emailText)}</div>`));
            } else if (emailSnippet) {
              setHtmlContent(getStyledHtml(`<div style="white-space: pre-wrap;"><em>Preview:</em><br/>${escapeHtml(emailSnippet)}</div>`));
            } else {
              setHtmlContent(getStyledHtml('<div>Failed to load email content.</div>'));
            }
          });
      } else if (emailHtmlUrl) {
        console.log('📄 Using HTML content directly for email:', email.id);
        setHtmlContent(getStyledHtml(sanitizeHtml(emailHtmlUrl)));
      } else if (emailText) {
        console.log('📝 Using text content for email:', email.id, '(no HTML available)');
        setHtmlContent(getStyledHtml(`<div style="white-space: pre-wrap;">${escapeHtml(emailText)}</div>`));
      } else if (emailSnippet) {
        console.log('📋 Using snippet content for email:', email.id, '(no HTML or text available)');
        setHtmlContent(
          getStyledHtml(
            `<div style="white-space: pre-wrap;"><em>Preview:</em><br/>${escapeHtml(emailSnippet)}</div>`
          )
        );
      } else {
        console.log('❌ No content available for email:', email.id);
        setHtmlContent(getStyledHtml('<div>No content available for this email.</div>'));
      }
    } else {
      // Reset state when modal is hidden or email is not present
      setHtmlContent(null);
      setAttachments([]);
    }
  }, [email, show]);

  const formatDate = (timestamp: any) => {
    try {
      let date: Date;
      
      // Handle null, undefined, or empty values
      if (!timestamp) {
        return 'No date';
      }
      
      // Handle Firestore Timestamp objects
      if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle JavaScript Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle numeric timestamps (milliseconds)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle string timestamps
      else if (typeof timestamp === 'string') {
        // Handle ISO strings and other common formats
        const parsedDate = new Date(timestamp);
        if (isNaN(parsedDate.getTime())) {
          return 'Invalid date format';
        }
        date = parsedDate;
      }
      // Handle objects with seconds/nanoseconds (Firestore format)
      else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle any other object format
      else if (typeof timestamp === 'object') {
        // Try to extract common timestamp properties
        if (timestamp.toISOString) {
          date = new Date(timestamp.toISOString());
        } else if (timestamp._seconds) {
          date = new Date(timestamp._seconds * 1000);
        } else {
          return 'Unknown date format';
        }
      }
      else {
        return 'Unsupported date type';
      }
      
      // Validate the final date
      if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error, 'timestamp:', timestamp);
      return 'Date error';
    }
  };

  // Chat-from-email handlers removed 2026-05.

  return (
    <>
      <div 
        className={`email-overlay ${show ? 'show' : ''}`}
        onClick={onHide}
      >
        <div className="email-overlay-content" onClick={(e) => e.stopPropagation()}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            email && (
              <div className="email-content-card">
                {/* Header */}
                <div className="email-header">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h4 className="text-gray-900 fw-bold mb-0">{email.subject}</h4>
                    <button 
                      type="button" 
                      className="btn-close" 
                      aria-label="Close"
                      onClick={onHide}
                    ></button>
                  </div>
                  
                  <div className="email-meta mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <strong className="text-gray-800 me-2">From:</strong>
                      <span className="text-gray-600">
                        {email.from?.[0]?.name || email.from?.[0]?.address || 'Unknown'}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <strong className="text-gray-800 me-2">Date:</strong>
                      <span className="text-gray-600">{formatDate(email.date)}</span>
                    </div>
                  </div>

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="attachments-section mb-3">
                      <strong className="text-gray-800 me-2">Attachments:</strong>
                      <div className="d-flex flex-wrap gap-2">
                        {attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-light-primary"
                          >
                            <i className="bi bi-paperclip me-1"></i>
                            {attachment.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 'Start Chat' action removed 2026-05. Open Andrei
                      via the header [andrei] button to discuss. */}
                </div>

                {/* Body */}
                <div className="email-body">
                  {htmlContent && (
                    <iframe
                      ref={iframeRef}
                      srcDoc={htmlContent}
                      // No allow-scripts: inbound email must never execute. allow-same-origin
                      // is only so adjustIframeHeight can measure the body (safe without
                      // scripts); the popups flags keep links opening in a new tab.
                      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                      style={{
                        width: '100%',
                        border: 'none',
                        backgroundColor: 'transparent',
                        minHeight: '200px',
                      }}
                      onLoad={adjustIframeHeight}
                      title="Email content"
                    />
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <style>
        {`
        .email-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 99999;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          backdrop-filter: blur(4px);
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .email-overlay.show {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }
        
        .email-overlay-content {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .email-content-card {
          background-color: var(--bs-body-bg);
          border-radius: var(--bs-border-radius);
          box-shadow: var(--bs-box-shadow-lg);
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .email-header {
          padding: 1.5rem;
          border-bottom: 1px solid var(--bs-border-color);
          background-color: var(--bs-gray-100);
          border-radius: var(--bs-border-radius) var(--bs-border-radius) 0 0;
          flex-shrink: 0;
        }
        
        .email-body {
          flex: 1;
          padding: 1.5rem;
          overflow-y: auto;
          background-color: var(--bs-body-bg);
        }
        
        .email-meta {
          font-size: 0.875rem;
        }
        
        .attachments-section {
          font-size: 0.875rem;
        }
        
        .email-actions {
          border-top: 1px solid var(--bs-border-color);
          padding-top: 1rem;
          margin-top: 1rem;
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .email-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 99999;
          }
          
          .email-overlay-content {
            width: 100%;
            height: 100%;
            padding: 0;
          }
          
          .email-content-card {
            width: 100%;
            height: 100%;
            border-radius: var(--bs-border-radius);
            box-shadow: var(--bs-box-shadow-lg);
          }
          
          .email-header,
          .email-body {
            padding: 1rem;
          }
        }
        `}
      </style>
    </>
  );
};

export default EmailOverlayModal; 